import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { MdClose } from 'react-icons/md';

export interface SelectableLayer {
  key: string;
  name: string;
}

interface Props {
  isOpen: boolean;
  surveyLabel: string;
  layers: SelectableLayer[];
  onConfirm: (selectedKeys: string[]) => void;
  onCancel: () => void;
}

const LayerSelectModal: React.FC<Props> = ({
  isOpen,
  surveyLabel,
  layers,
  onConfirm,
  onCancel,
}) => {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(layers.map((l) => l.key)),
  );

  // Reset selection when the layer list changes (new survey)
  useEffect(() => {
    setSelected(new Set(layers.map((l) => l.key)));
  }, [layers]);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      bodyOpenClassName={null}
      portalClassName="react-modal-container"
      overlayClassName="react-modal-overlay"
      className="react-modal-wrapper"
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
    >
      <div className="react-modal playback-layer-select-modal">
        <div className="modal-header">
          <div className="modal-title-container">
            <div className="modal-title-in-container">Select Layers to Play</div>
          </div>
          <div className="modal-header-buttons">
            <div className="modal-close" onClick={onCancel}>
              <MdClose />
            </div>
          </div>
        </div>

        <div className="modal-content playback-layer-select-content">
          <p className="playback-layer-select-hint">
            Select which image layers to display during playback
            <strong>{surveyLabel}</strong>.
          </p>
          <div className="playback-layer-select-list">
            {layers.map((layer) => {
              const isChecked = selected.has(layer.key);
              return (
                <label
                  key={layer.key}
                  className="playback-layer-select-item"
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggle(layer.key)}
                  />
                  <span>{layer.name}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="modal-footer playback-layer-select-footer">
          <button className="xa-button secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="xa-button primary"
            onClick={() => onConfirm(Array.from(selected))}
            disabled={selected.size === 0}
          >
            Start Playback
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LayerSelectModal;
